import React, { useState } from 'react';

import { DialogActions, DialogContent, FormControlLabel, Radio, RadioGroup } from '@mui/material';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Typography from 'src/components/ui/Typography';
import Button from 'src/components/ui/Button';
import IconButton from 'src/components/ui/IconButton';

import { colours } from 'src/constants/colours';
import { breakpoints } from 'src/constants/breakpoints';
import { styledComponent } from 'src/utils/styled';

const StyledDialog = styledComponent(Dialog)`
    width: fit-content;
    margin: auto;
    .MuiPaper-root {
        padding: 1rem;
    }
    @media (max-width: ${breakpoints.tabletPortrait - 1}px) {
        width: 100vw;
        .MuiPaper-root {
            padding: 0;
            width: 100%;
            height: 100%;
            max-height: 100%;
            margin: 0;
            border-radius: 0;
            display: flex;
            justify-content: center;
            align-items: center;
        }
    }
`;

const StyledDialogActions = styledComponent(DialogActions)`
    padding: 0.5rem;
`;

const StyledDialogContent = styledComponent(DialogContent)`
    width: 22.75rem;
`;

const StyledDialogTitle = styledComponent(DialogTitle)`
    padding-top: 0.3rem;
`;

const StyledFormControlLabel = styledComponent(FormControlLabel)`
    padding: 0.2rem;
    .Mui-checked {
        ~ .MuiFormControlLabel-label {
            font-weight: bold;
        }
    }
`;

const StyledButton = styledComponent(Button)`
    width: 6.25rem;
`;

const SkipText = styledComponent(Typography)`
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
`;

const CloseButton = styledComponent(IconButton)`
    position: absolute;
    top: 1rem;
    right: 1rem;
    z-index: 10;
    padding: 0.2rem;
    background-color: ${colours.white.main};
`;

export interface Item {
    value: string | null;
    label: string;
    name?: string;
    dataTarget?: string;
    dataTargetId?: string;
}

interface Props {
    open: boolean;
    title: string;
    description?: string;
    defaultValue?: string | null;
    items: Item[];
    showCancel?: boolean;
    showSkip?: boolean;
    modalDataTarget?: string;
    onClose: () => void;
    onChange?: (value: string | null) => void;
    onConfirm: (value: string | null) => void;
    onCancel?: () => void;
    onSkip?: () => void;
}

const RadioGroupModal: React.FC<Props> = ({
    open,
    title,
    description,
    items,
    defaultValue = null,
    showCancel,
    showSkip,
    modalDataTarget = 'radio-group-modal',
    onClose,
    onChange,
    onConfirm,
    onCancel,
    onSkip,
}) => {
    const [value, setValue] = useState(defaultValue);
    const { t } = useTranslation(['common']);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
        if (onChange) onChange(e.target.value);
    };

    const handleConfirm = () => {
        onClose();
        onConfirm(value);
    };

    const handleCancel = () => {
        onClose();
        setValue(defaultValue);
        if (onCancel) onCancel();
    };

    const handleSkip = () => {
        onClose();
        if (onSkip) onSkip();
    };

    return (
        <StyledDialog open={open} onClose={onClose}>
            <Box>
                <CloseButton
                    data-target={`${modalDataTarget}-button`}
                    data-target-id={`${modalDataTarget}-close`}
                    color="white"
                    onClick={onClose}
                    iconProps={{
                        name: 'Close',
                    }}
                />
                <DialogTitle>
                    <Typography
                        data-target={`${modalDataTarget}-text`}
                        data-target-id={`${modalDataTarget}-title`}
                        variant="h2"
                    >
                        {title}
                    </Typography>
                </DialogTitle>
                {description && (
                    <StyledDialogTitle>
                        <Typography
                            data-target={`${modalDataTarget}-text`}
                            data-target-id={`${modalDataTarget}-description`}
                            variant="h4"
                        >
                            {description}
                        </Typography>
                    </StyledDialogTitle>
                )}
                <StyledDialogContent>
                    <RadioGroup onChange={handleChange} value={value}>
                        {items.map((item) => (
                            <StyledFormControlLabel
                                key={item.value}
                                data-target={item.dataTarget}
                                data-target-id={item.dataTargetId}
                                name={item.name}
                                value={item.value}
                                label={item.label}
                                control={<Radio />}
                            />
                        ))}
                    </RadioGroup>
                </StyledDialogContent>
                <StyledDialogActions>
                    {showSkip && (
                        <Box mx="1rem">
                            <SkipText
                                data-target={`${modalDataTarget}-button`}
                                data-target-id={`${modalDataTarget}-skip`}
                                onClick={handleSkip}
                            >
                                {t(`common:modal.skip`)}
                            </SkipText>
                        </Box>
                    )}
                    {showCancel && (
                        <StyledButton
                            data-target={`${modalDataTarget}-button`}
                            data-target-id={`${modalDataTarget}-cancel`}
                            variant="outlined"
                            onClick={handleCancel}
                        >
                            {t(`common:modal.cancel`)}
                        </StyledButton>
                    )}
                    <StyledButton
                        disabled={value === null}
                        data-target={`${modalDataTarget}-button`}
                        data-target-id={`${modalDataTarget}-confirm`}
                        onClick={handleConfirm}
                    >
                        {t(`common:modal.confirm`)}
                    </StyledButton>
                </StyledDialogActions>
            </Box>
        </StyledDialog>
    );
};

export default RadioGroupModal;
